import { Button, Card, CardContent, CardHeader, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { apiConstants, get, post } from "../../common/constants";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatCurrency, formatDate1 } from "../../common/util";
import { FormProvider, RHFFileField, RHFSelectField, RHFTextField } from "../../common/hook-form";
import RHFSearchableSelectField from "../../common/hook-form/RHFSearchableSelectField";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import MUIModal from "../../components/MuiModal";
import PageTitle from "../../components/PageTitle";
import Loader from "../../common/Loader";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import DownloadSampleFile from "../../components/DownloadSampleFile";

function CourierInvoice() {
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [reload, setReload] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);

    const courierFilter = useCourierParentNames();

    const methodsFilter = useForm({
        defaultValues: {
            courierParentName: '',
            month: '',
            year: '',
        }
    });
    const { handleSubmit: handleSubmitFilter, reset: resetForm } = methodsFilter;

    const [filterParams, setFilterParams] = useState({});

    const months = [
        { label: 'January', value: 1 }, { label: 'February', value: 2 }, { label: 'March', value: 3 },
        { label: 'April', value: 4 }, { label: 'May', value: 5 }, { label: 'June', value: 6 },
        { label: 'July', value: 7 }, { label: 'August', value: 8 }, { label: 'September', value: 9 },
        { label: 'October', value: 10 }, { label: 'November', value: 11 }, { label: 'December', value: 12 }
    ];

    const onSubmitFilter = (data) => {
        const params = {};
        if (data.courierParentName) params.courierParentName = data.courierParentName;
        if (data.month) params.invoiceMonth = data.month;
        if (data.year) params.invoiceYear = data.year;
        setFilterParams(params);
        setPage(0);
    };

    useEffect(() => {
        setLoading(true);
        const params = {
            ...filterParams,
            page: page,
            size: sizePerPage,
        };
        get(apiConstants.COURIER_ORDER_INVOICE_LIST, params)
            .then((res) => {
                console.log(res);

                const response = res?.data?.responseObj;
                setData(response?.data || []);
                setTotalCount(response?.totalCount || 0);
                setLoading(false);
            })
            .catch(() => {
                setData([]);
                setLoading(false);
            });
    }, [reload, sizePerPage, page, filterParams]);

    const handleClose = () => {
        setUploadOpen(false);
    };

    const columns = [
        {
            field: 'courierParentName', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Courier', flex: 1,
        },
        {
            field: 'invoiceNumber', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Invoice Number', flex: 1,
        },
        {
            field: 'invoiceMonth', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Invoice Period', flex: 1,
            renderCell: (params) => {
                const { row } = params;
                const monthObj = months.find(m => m.value === row?.invoiceMonth);
                return `${monthObj?.label || row?.invoiceMonth} ${row?.invoiceYear || ''}`;
            },
        },
        {
            field: 'fromDate', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'From Date', flex: 0.8,
            renderCell: (params) => formatDate1(params.row?.fromDate, "dd MMM, yyyy"),
        },
        {
            field: 'toDate', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'To Date', flex: 0.8,
            renderCell: (params) => formatDate1(params.row?.toDate, "dd MMM, yyyy"),
        },
        {
            field: 'totalOrders', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Total Orders', flex: 0.6,
            renderCell: (params) => params.row?.totalOrders || 0,
        },
        {
            field: 'totalAmount', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Total Amount', flex: 0.8,
            renderCell: (params) => {
                const amt = params.row?.totalAmount;
                return amt ? formatCurrency(amt, 0) : formatCurrency(0);
            },
        },
        {
            field: 'version', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Version', flex: 0.4,
        },
        {
            field: 'createdAt', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Created Date', flex: 0.8,
            renderCell: (params) => formatDate1(params.row?.createdAt, "dd MMM, yyyy"),
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <PageTitle>Courier Invoice</PageTitle>

            <MUIModal
                open={uploadOpen}
                handleClose={handleClose}
                title={"Upload Courier Invoice"}
                children={
                    <UploadCourierInvoice
                        courierOptions={courierFilter}
                        handleClose={handleClose}
                        setReload={setReload}
                    />
                }
            />

            <Card sx={{ mb: 2 }}>
                <CardHeader
                    action={
                        <Button
                            variant="contained"
                            size="small"
                            color="warning"
                            startIcon={<FileUploadIcon />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload Invoice
                        </Button>
                    }
                />
                <CardContent>
                    <FormProvider methods={methodsFilter} onSubmit={handleSubmitFilter(onSubmitFilter)}>
                        <Grid container spacing={1} mt={1}>
                            <Grid item xs={12} md={3}>
                                <RHFSearchableSelectField
                                    name='courierParentName'
                                    label="Select Courier"
                                    options={courierFilter}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFSelectField
                                    name='month'
                                    label="Select Month"
                                    options={months}
                                    width='100%'
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFTextField name='year' label='Year' />
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                <LoadingButton variant="contained" size="small" type="submit">
                                    Submit
                                </LoadingButton>
                                <Button
                                    onClick={() => {
                                        resetForm({ courierParentName: '', month: '', year: '' });
                                        setFilterParams({});
                                        setPage(0);
                                    }}
                                    variant="contained"
                                    color='error'
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>

            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={data || []}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                pagination={true}
                hideFooterPagination={false}
                filterMode={"server"}
                rowHeight={60}
                rowCount={totalCount}
                getRowId={(row) => row.id}
            />
        </>
    );
}

export default CourierInvoice;


function useCourierParentNames() {
    const filter = {
        andfilter: [{ field: "is_deleted", operator: "eq", value: '0' }],
        offset: 0,
        limit: 1000,
        skipCount: true,
        includes: "id,name,parent"
    };
    const { data } = useGetAllCourierPartnersCachedQuery(filter);

    const uniqueParents = _.uniqBy(
        (data || []).filter(c => c.parent).map(c => ({ value: c.parent, label: c.parent })),
        'value'
    );
    return _.orderBy(uniqueParents, 'label');
}


const UploadCourierInvoice = ({ courierOptions, handleClose, setReload }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [submitting, setSubmitting] = useState(false);

    const months = [
        { label: 'January', value: 1 }, { label: 'February', value: 2 }, { label: 'March', value: 3 },
        { label: 'April', value: 4 }, { label: 'May', value: 5 }, { label: 'June', value: 6 },
        { label: 'July', value: 7 }, { label: 'August', value: 8 }, { label: 'September', value: 9 },
        { label: 'October', value: 10 }, { label: 'November', value: 11 }, { label: 'December', value: 12 }
    ];

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                courierParentName: Yup.string().required("Please select courier"),
                invoiceNumber: Yup.string().required("Please enter invoice number"),
                invoiceMonth: Yup.number().typeError("Please select invoice month").required("Please select invoice month"),
                invoiceYear: Yup.number().typeError("Please enter a valid year").required("Please enter invoice year"),
                totalOrders: Yup.number().typeError("Please enter a valid number").required("Please enter total orders"),
                totalAmount: Yup.number().typeError("Please enter a valid amount").required("Please enter total amount"),
                file: Yup.mixed().required("Please select file"),
            })
        ),
        defaultValues: {
            courierParentName: '',
            invoiceNumber: '',
            invoiceMonth: '',
            invoiceYear: '',
            totalOrders: '',
            totalAmount: '',
        }
    });

    const { handleSubmit, reset } = methods;

    const onSubmit = async (data) => {
        setSubmitting(true);
        let formData = new FormData();
        formData.append('file', data.file[0]);
        formData.append('courierParentName', data.courierParentName);
        formData.append('invoiceNumber', data.invoiceNumber);
        formData.append('invoiceMonth', data.invoiceMonth);
        formData.append('invoiceYear', data.invoiceYear);
        formData.append('totalOrders', data.totalOrders);
        formData.append('totalAmount', data.totalAmount);
        try {
            const res = await post(apiConstants.COURIER_ORDER_INVOICE_UPLOAD, formData);
            if (res?.data?.status === "SUCCESS") {
                enqueueSnackbar("Invoice uploaded successfully", { variant: 'success' });
                setReload(r => r + 1);
                handleClose();
                reset();
            } else {
                enqueueSnackbar(res?.data?.errorMsg || 'Some error occurred', { variant: 'error' });
            }
        } catch (err) {
            console.log(err);
            enqueueSnackbar("Some error occurred", { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
                <Grid item xs={12} md={4}>
                    <RHFSearchableSelectField
                        name='courierParentName'
                        label="Select Courier"
                        options={courierOptions}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFTextField name='invoiceNumber' label='Invoice Number *' placeholder='e.g. INV-2024-001' />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFSelectField name='invoiceMonth' label='Invoice Month *' options={months} width='100%' />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFTextField name='invoiceYear' label='Invoice Year *' placeholder='e.g. 2024' />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFTextField name='totalOrders' label='Total Orders *' placeholder='e.g. 100' />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RHFTextField name='totalAmount' label='Total Amount *' placeholder='e.g. 5000.00' />
                </Grid>
                <Grid item xs={12} md={6}>
                    <RHFFileField name='file' fileType={['csv']} />
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton variant="contained" size="medium" type="submit" loading={submitting}>
                        Submit
                    </LoadingButton>
                    <Button onClick={() => reset()} variant="contained" color='error' size="medium" sx={{ ml: 1, mr: 1 }}>
                        Reset
                    </Button>
                    <DownloadSampleFile fileName="FINANCE_UPLOAD_INVOICE_SAMPLE" />
                </Grid>
            </Grid>
        </FormProvider>
    );
};
